/**
 * TOML Validate Tool
 *
 * Validates TOML syntax and reports errors with details.
 */

import { parse as parseToml } from 'smol-toml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const TomlValidateTool: ITransformerTool = {
  id: 'toml-validate',
  name: 'TOML Validate',
  description: 'Validate TOML syntax and report any errors',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['toml', 'validate', 'check', 'verify', 'lint', 'syntax'],

  seo: {
    slug: 'toml-validator',
    pageTitle: 'TOML Validator - Free Online Syntax Checker | SWE DevTools',
    metaDescription: 'Validate TOML syntax and see detailed error messages with line numbers. Check configuration files instantly. Fast, private, runs in your browser.',
    keywords: ['toml validator', 'toml syntax checker', 'validate toml online', 'toml lint', 'toml checker'],
    relatedTools: ['toml-to-json', 'toml-to-yaml', 'toml-format'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'Text';
  },

  async transform(input: string): Promise<string> {
    try {
      const parsed = parseToml(input);
      const keys = Object.keys(parsed);
      const tables = keys.filter(k => typeof parsed[k] === 'object' && parsed[k] !== null && !Array.isArray(parsed[k]));
      const arrays = keys.filter(k => Array.isArray(parsed[k]));
      const scalars = keys.filter(k => typeof parsed[k] !== 'object' || parsed[k] === null);

      const details: string[] = [];
      details.push(`Top-level keys: ${keys.length}`);
      if (tables.length > 0) details.push(`Tables: ${tables.length}`);
      if (arrays.length > 0) details.push(`Arrays: ${arrays.length}`);
      if (scalars.length > 0) details.push(`Scalar values: ${scalars.length}`);

      return `✓ Valid TOML\n${details.join('\n')}`;
    } catch (e) {
      const error = e as Error;
      return `✗ Invalid TOML\nError: ${error.message}`;
    }
  },
};
